import React, { useEffect, useState } from 'react';
import { createFormBag, FormBag, FormErrors, validateFormBag } from 'react-formage'

import { Button, ButtonGroup } from 'components/ui/button'
import { Modal } from 'components/ui/modal'

import { sharedActionCreators, SharedActionCreators } from 'containers/shared';
import { connect } from 'containers/store';

import {
  actionCreators as linkableActionCreators,
  LinkableActionCreators,
} from 'domains/linkable/actions'
import { LinkableStore } from 'domains/linkable/store'
import { actionCreators, GettyActionCreators } from 'domains/org/getty/actions';
import { GettyStore } from 'domains/org/getty/store';

import { Stop } from 'generated/mos/org/getty/audio'
import { CreateStopRequest } from 'generated/mos/org/getty/audiomanagement'
import { TMSObject } from 'generated/mos/org/getty/integrationtms'

import { Invariant, pick } from 'helpers/core'
import { convertLinkedFieldMappingsToValues, extractSourceAttributeNames } from 'helpers/linkables'
import { Status } from 'helpers/status'

import { SelectStopType } from './select-stop-type';
import { SelectTmsObject } from './select-tms-object'
import { StopFromScratch } from './stop-from-scratch';
import { stopSchema } from '../../../schemas/stop'

type Step = {
  header: React.ReactNode;
  body: React.ReactNode;
  footer: React.ReactNode;
};

export type Steps = {
  selectStopType: Step;
  createStopFromScratch: Step;
  'mos.org.getty.integration_tms.TMSObject': Step;
};

export type FormValues = {
  title: string;
};

export const validate = (values: FormValues) => {
  const errors: FormErrors<FormValues> = {};
  if (!values.title.trim()) {
    errors.title = 'Title required';
  }
  return errors;
};

type ConnectedProps = {
  fieldMappings: LinkableStore['linkable']['fieldMappings'];
  stopCreate: GettyStore['getty']['stopCreate'];
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyCreateStopRequest' | 'gettyCreateStopReset'> &
  Pick<LinkableActionCreators, 'linkableGetLinkedFieldMappingsRequest' | 'linkableGetLinkedFieldMappingsReset'> &
  Pick<SharedActionCreators, 'toastNotification'>;

type DirectProps = {
  onCancel: () => void;
  onComplete: (stop: Stop.Entity) => void;
  tourRefs?: CreateStopRequest.Entity['tourRefs'];
  moodRefs?: CreateStopRequest.Entity['moodRefs'];
};

type Props = ConnectedProps & ActionProps & DirectProps;

const StopCreateView = (props: Props) => {
  const {
    fieldMappings,
    gettyCreateStopRequest,
    gettyCreateStopReset,
    linkableGetLinkedFieldMappingsRequest,
    linkableGetLinkedFieldMappingsReset,
    onComplete,
    stopCreate,
    toastNotification,
  } = props

  const [ selectedType, setSelectedType ] = useState<keyof Steps>()
  const [ currentStep, setCurrentStep ] = useState<keyof Steps>('selectStopType')
  const [ bag, setFormBag ] = useState<FormBag<FormValues>>(createFormBag({ title: '' }))
  const [ tmsObjectSelected, setTmsObjectSelected ] = useState<TMSObject.Entity | undefined>()

  const updateSelectedType = (type: keyof Steps) => {
    // available target types get loaded from the backend, so check we can handle them.
    if (!steps[type]) {
      throw new Invariant('unhandled')
    }
    setSelectedType(type)
  }

  useEffect(() => {
    // create a tms based stop now that we have the field mappings results.
    if (fieldMappings.status === Status.Ready && stopCreate.status === Status.Idle) {
      if (!tmsObjectSelected) {
        throw new Error('TMS based stop creation triggered but no TMS object selected.')
      }

      // reference to the linked entity, used on the stop entity and each attribute ref.
      const basedOnRef = {
        id: TMSObject.mustRef(tmsObjectSelected.ref).id,
        typename: 'mos.org.getty.integration_tms.TMSObject' as const
      }

      // convert the mappings response into assembled values to spread into a stop entity.
      const attributeRefValues = convertLinkedFieldMappingsToValues<Stop.Entity>(
        fieldMappings.data.mappings,
        stopSchema,
        basedOnRef,
      )

      // assemble the new stop entity.
      const newStop: Stop.Entity = {
        ...Stop.defaults,
        ...attributeRefValues,
        basedOnRef,
        tourRefs: props.tourRefs ? props.tourRefs : [],
        moodRefs: props.moodRefs ? props.moodRefs : [],
      }

      // strip out the stop ref and type to form the create stop payload.
      const { ref, type, ...payload } = newStop
      gettyCreateStopRequest({
        type: CreateStopRequest.refName,
        ...payload,
      })
    }

    // retrieving field mappings or creating a stop has failed.
    if (fieldMappings.status === Status.Failed || stopCreate.status === Status.Failed) {
      toastNotification({
        type: 'error',
        text: 'Could not create stop',
      });
      gettyCreateStopReset()
      linkableGetLinkedFieldMappingsReset()
    }
  }, [ fieldMappings.status, stopCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // creating a stop was successful.
    if (stopCreate.status === Status.Ready) {
      onComplete(stopCreate.data);
      gettyCreateStopReset();
      linkableGetLinkedFieldMappingsReset()
    }
  }, [ stopCreate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  const steps: Steps = {
    selectStopType: {
      header: <h3>Create a new stop</h3>,
      body: (
        <SelectStopType
          selectedType={selectedType}
          setSelectedType={updateSelectedType}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button
            onClick={() => selectedType && setCurrentStep(selectedType)}
            isDisabled={!selectedType}
          >
            Continue
          </Button>
        </ButtonGroup>
      )
    },
    createStopFromScratch: {
      header: <h3>Create a new stop</h3>,
      body: (
        <StopFromScratch
          bag={bag}
          setFormBag={setFormBag}
          stopCreate={stopCreate}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!bag.valid}
            onClick={() => {
              const validatedBag = validateFormBag(bag, validate)
              setFormBag(validatedBag)

              if (validatedBag.valid) {
                gettyCreateStopRequest({
                  ...CreateStopRequest.defaults,
                  title: stopSchema.title ? stopSchema.title.write(bag.values.title) : bag.values.title,
                  tourRefs: props.tourRefs ? props.tourRefs : [],
                  moodRefs: props.moodRefs ? props.moodRefs : [],
                })
              }
            }}
          >
            Create stop
          </Button>
        </ButtonGroup>
      )
    },
    'mos.org.getty.integration_tms.TMSObject': {
      header: <h3>Find TMS objects</h3>,
      body: (
        <SelectTmsObject
          tmsObjectSelected={tmsObjectSelected}
          onUpdate={(entity) => setTmsObjectSelected(entity)}
        />
      ),
      footer: (
        <ButtonGroup>
          <Button onClick={props.onCancel} appearance="secondary">Cancel</Button>
          <Button
            isDisabled={!tmsObjectSelected || fieldMappings.status === Status.Loading}
            // on successful response from field mapping service a tms based stop entity will be
            // assembled to submit to the api.
            onClick={() => linkableGetLinkedFieldMappingsRequest({
              type: 'mos.linked_field_mappings.GetLinkedFieldMappingsRequest',
              sourceTypename: 'mos.org.getty.audio.Stop',
              targetTypename: 'mos.org.getty.integration_tms.TMSObject',
              relationshipName: 'based_on',
              sourceAttributeNames: extractSourceAttributeNames(Stop.defaults)
            })}
          >
            Create stop
          </Button>
        </ButtonGroup>
      ),
    },
  };

  return (
    <Modal
      onClose={props.onCancel}
      header={steps[currentStep].header}
      footer={steps[currentStep].footer}
    >
      {steps[currentStep].body}
    </Modal>
  );
};

export const StopCreate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore>(
  store => ({
    fieldMappings: store.linkable.fieldMappings,
    stopCreate: store.getty.stopCreate
  }),
  {
    ...pick(actionCreators, 'gettyCreateStopRequest', 'gettyCreateStopReset'),
    ...pick(
      linkableActionCreators,
      'linkableGetLinkedFieldMappingsRequest',
      'linkableGetLinkedFieldMappingsReset'
    ),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(StopCreateView)
